import { Button, Flex, Heading, Spacer, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { RiTranslate2 } from "react-icons/ri";
import { useParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { NotFound } from "src/components/Feedback/NotFound";
import { RawHtmlViewer } from "src/components/HtmlViewer";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useBranding } from "src/components/Providers/BrandingProvider";
import {
  RecipientsTable,
  RecipientsTableDataType,
} from "src/components/Table/RecipientsTable";
import {
  convertAttachmentToFile,
  getNonInlineAttachmentCids,
  replaceCIDsWithInlineImages,
} from "src/components/WysiwygEditor/lexical/utils/converters";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import { useMessageRichContentQuery } from "src/hooks/useMessageRichContent";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSchoolAdminPermissions } from "src/hooks/useSchoolAdminPermissions";
import * as breadcrumb from "src/services/breadcrumb";
import * as GQL from "src/types/graphql";
import * as EmailBody from "src/types/messages/clientBranding/emailBody";
import * as RD from "src/types/remoteData";
import { AttachmentsList } from "./components/AttachmentsList";
import { GET_MESSAGE_BY_ID } from "./graphql/queries";

export const ViewMessage: React.FC = () => {
  const { id } = useParams();
  const organization = useOrganization();
  const { messageAdhocReceiverFilter } = useSchoolAdminPermissions();
  const getLanguageName = useGetEnglishLanguageName();

  const { remoteData } = useRemoteDataQuery<
    GQL.GetMessageById,
    GQL.GetMessageByIdVariables
  >(GET_MESSAGE_BY_ID, {
    variables: {
      message_id: id ?? "",
      message_receiver_filter: messageAdhocReceiverFilter(),
    },
    skip: !id,
  });

  const { remoteData: richContentRemoteData } = useMessageRichContentQuery(
    remoteData.hasData()
      ? remoteData.data?.message_adhoc_by_pk
          ?.message_adhoc_payload_rich_contents?.[0]?.content_id
      : null,
    !remoteData.hasData() // skip retreiving rich content the message is not loaded
  );

  const [fileAttachments, setFileAttachments] = useState<File[]>([]);
  useEffect(() => {
    // Get the file attachments that are not inline, and convert to File type
    if (richContentRemoteData.hasData()) {
      const rc = richContentRemoteData.data;
      const nonInlineAttachments = getNonInlineAttachmentCids(rc) ?? [];
      Promise.all(
        nonInlineAttachments.map((attachment) =>
          convertAttachmentToFile(attachment)
        )
      ).then((files) => {
        setFileAttachments(files);
      });
    }
  }, [richContentRemoteData]);

  const branding = useBranding();

  if (!id) return <NotFound />;

  return (
    <RemoteDataView
      remoteData={RD.toTuple(remoteData, richContentRemoteData)}
      error={() => <GenericError />}
      loading={<Loading />}
    >
      {([data, richContent]) => {
        if (data.message_adhoc_by_pk === null) return <NotFound />;

        const recipients: RecipientsTableDataType[] = []; // do this way to eliminate nullables
        data.message_adhoc_by_pk.message_adhoc_form_receivers.forEach(
          (receiver) => {
            const recipient = receiver.receiver_person;
            if (recipient != null) {
              recipients.push({
                id: recipient.id,
                full_name: `${recipient.first_name ?? ""} ${
                  recipient.last_name ?? ""
                }`.trim(),
                contact_info: receiver.receiver_contact_information ?? "",
                preferred_language: recipient.preferred_language,
                message_status: receiver.message_status,
                applicant_name: receiver.applicant_person?.first_name ?? "",
              });
            }
          }
        );

        const emailHtml = EmailBody.template(
          branding,
          richContent?.html
            ? replaceCIDsWithInlineImages(
                richContent.html,
                richContent.attachments
              )
            : data.message_adhoc_by_pk.message_adhoc_payload_email
                ?.message_body ?? ""
        );

        return (
          <Flex direction="column" gap={8} width="100%">
            <Breadcrumb
              items={breadcrumb.messages.getBreadcrumbsForView(
                organization,
                data.message_adhoc_by_pk
              )}
            />
            <Heading as="h1" size="lg">
              Messages
            </Heading>

            <Flex gap={6}>
              <Flex width="60%">
                <RecipientsTable recipients={recipients} />
              </Flex>

              <Flex direction="column" gap={4} width="40%">
                {data.message_adhoc_by_pk.message_adhoc_payload_email && (
                  <>
                    <Flex direction="row">
                      <Spacer />
                      {!!richContent?.language && (
                        <Button
                          variant="outline"
                          size="sm"
                          color="gray.700"
                          fontWeight="400"
                          borderColor="blackAlpha.200"
                          leftIcon={<RiTranslate2 size="16px" />}
                        >
                          {richContent.language === "en"
                            ? `Showing original English`
                            : `Showing sent ${getLanguageName(
                                richContent.language
                              )} translation`}
                        </Button>
                      )}{" "}
                    </Flex>
                    <Flex direction="column">
                      <Text color="gray.800" fontSize="sm">
                        Subject line
                      </Text>
                      <Text color="gray.600">
                        {
                          data.message_adhoc_by_pk.message_adhoc_payload_email
                            ?.message_subject
                        }
                      </Text>
                    </Flex>
                    <Flex color="gray.800" direction="column">
                      <Text fontSize="sm">Email content</Text>
                      <RawHtmlViewer
                        border="1px solid gray.200"
                        minHeight="30rem"
                        html={emailHtml}
                      />
                    </Flex>
                    <Flex direction="column">
                      <Text color="gray.800" fontSize="sm">
                        Email attachments
                      </Text>
                      <AttachmentsList
                        attachments={fileAttachments}
                        viewMode={true}
                      />
                    </Flex>
                  </>
                )}

                {data.message_adhoc_by_pk.message_adhoc_payload_sms
                  ?.message_body && (
                  <>
                    <Flex direction="column">
                      <Text color="gray.800" fontSize="sm">
                        SMS content
                      </Text>
                      <Text color="gray.600">
                        {
                          data.message_adhoc_by_pk.message_adhoc_payload_sms
                            ?.message_body
                        }
                      </Text>
                    </Flex>
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
        );
      }}
    </RemoteDataView>
  );
};
